import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import {
  Package, Search, CheckCircle, Clock, Truck, XCircle, Euro,
  ArrowLeft, AlertCircle, ShoppingBag, FileText, MessageSquare,
  ArrowRight, RefreshCw
} from 'lucide-react'
import LanguageSwitcher from '../components/LanguageSwitcher'
import VacationNotice from '../components/VacationNotice'

// URL base de la API
const API_BASE = ''

interface TrackingData {
  code: string
  case_type?: string
  order_reference: string
  status: string
  status_label: string
  created_at: string
  product_amount: number
  shipping_cost: number
  refund_amount: number
  refund_adjustment_type: string | null
  original_order_shipping: number | null
  pickup_tracking: string | null
  carrier_code: string | null
  replacement_tracking: string | null
  replacement_carrier: string | null
  rejection_reason: string | null
  refunded_at: string | null
  refund_method: string | null
  transfer_receipt: string | null
  history: Array<{
    status: string
    status_label: string
    description: string
    created_at: string
  }>
}

const STATUS_CONFIG: Record<string, { color: string; icon: React.ElementType; bgColor: string }> = {
  requested: { color: 'text-yellow-600', icon: Clock, bgColor: 'bg-yellow-100' },
  approved: { color: 'text-blue-600', icon: CheckCircle, bgColor: 'bg-blue-100' },
  rejected: { color: 'text-red-600', icon: XCircle, bgColor: 'bg-red-100' },
  pickup_scheduled: { color: 'text-cyan-600', icon: Truck, bgColor: 'bg-cyan-100' },
  in_transit: { color: 'text-purple-600', icon: Truck, bgColor: 'bg-purple-100' },
  received: { color: 'text-indigo-600', icon: Package, bgColor: 'bg-indigo-100' },
  tested: { color: 'text-teal-600', icon: CheckCircle, bgColor: 'bg-teal-100' },
  refunded: { color: 'text-green-600', icon: Euro, bgColor: 'bg-green-100' },
  replacement: { color: 'text-orange-600', icon: RefreshCw, bgColor: 'bg-orange-100' },
  refund_rejected: { color: 'text-rose-600', icon: XCircle, bgColor: 'bg-rose-100' },
  warranty_approved: { color: 'text-green-600', icon: CheckCircle, bgColor: 'bg-green-100' },
  warranty_rejected: { color: 'text-red-600', icon: XCircle, bgColor: 'bg-red-100' },
  closed: { color: 'text-slate-600', icon: CheckCircle, bgColor: 'bg-slate-100' },
  cancelled: { color: 'text-slate-600', icon: XCircle, bgColor: 'bg-slate-100' }
}

const CARRIER_TRACKING_URLS: Record<string, string> = {
  mrw: 'https://www.mrw.es/seguimiento_envios/MRW_historico_envios.asp?enviession=',
  gls: 'https://www.gls-spain.es/es/seguimiento-de-envios/?match=',
  ctt: 'https://www.cttexpress.com/seguimiento/?numero='
}

const LOCALE_MAP: Record<string, string> = { es: 'es-ES', pt: 'pt-PT', en: 'en-GB' }

function getApiError(detail: any, t: any): string {
  if (typeof detail === 'object' && detail?.code) {
    const key = `errors.api.${detail.code}`
    const translated = t(key, { defaultValue: '', ...detail.params })
    return translated || detail.message || t('errors.generic')
  }
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || JSON.stringify(e)).join('; ')
  return t('errors.generic')
}

export default function PublicReturnTrackingPage() {
  const { t, i18n } = useTranslation()
  const locale = LOCALE_MAP[i18n.language] || 'en-GB'
  const { code: urlCode } = useParams<{ code: string }>()

  // Formulario de busqueda
  const [searchType, setSearchType] = useState<'online' | 'invoice'>('online')
  const [code, setCode] = useState(urlCode || '')
  const [email, setEmail] = useState('')
  const [nif, setNif] = useState('')

  // Estado
  const [tracking, setTracking] = useState<TrackingData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Si viene codigo en URL, mostrar formulario de verificacion
  useEffect(() => {
    if (urlCode) {
      setCode(urlCode)
    }
  }, [urlCode])

  const fetchTracking = async () => {
    if (!code.trim()) {
      setError(t('tracking.errorMissingCode'))
      return
    }

    if (searchType === 'online' && !email.trim()) {
      setError(t('tracking.errorMissingEmail'))
      return
    }

    if (searchType === 'invoice' && !nif.trim()) {
      setError(t('tracking.errorMissingNif'))
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_BASE}/api/returns/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          email: searchType === 'online' ? email.trim() : undefined,
          nif: searchType === 'invoice' ? nif.trim() : undefined
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(getApiError(data.detail, t))
      }

      setTracking(data)
      setIsAuthenticated(true)
      // Actualizar URL sin recargar
      window.history.pushState({}, '', `/returns/track/${code.trim()}`)
    } catch (err: any) {
      setError(err.message || t('tracking.errorGeneric'))
      setTracking(null)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    fetchTracking()
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const getTrackingUrl = () => {
    if (!tracking?.carrier_code || !tracking?.pickup_tracking) return null
    const baseUrl = CARRIER_TRACKING_URLS[tracking.carrier_code.toLowerCase()]
    if (!baseUrl) return null
    return baseUrl + tracking.pickup_tracking
  }

  const getReplacementTrackingUrl = () => {
    if (!tracking?.replacement_carrier || !tracking?.replacement_tracking) return null
    const baseUrl = CARRIER_TRACKING_URLS[tracking.replacement_carrier.toLowerCase()]
    if (!baseUrl) return null
    return baseUrl + tracking.replacement_tracking
  }

  // Translate status label from backend or use our translations
  const getStatusLabel = (status: string, backendLabel?: string) => {
    const key = `statuses.${status}`
    const translated = t(key)
    return translated !== key ? translated : (backendLabel || status)
  }

  const statusConfig = tracking ? STATUS_CONFIG[tracking.status] || STATUS_CONFIG.requested : null
  const StatusIcon = statusConfig?.icon || Clock

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <VacationNotice variant="returns" />
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-600/20">
                <span className="text-white font-bold text-lg">J</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">JVS Informatica</h1>
                <p className="text-sm text-slate-500">{t('common.trackingTitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Formulario de busqueda (solo si no está autenticado) */}
        {!isAuthenticated && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-4">{t('tracking.searchTitle')}</h2>

            {/* Selector de tipo */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button
                type="button"
                onClick={() => setSearchType('online')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  searchType === 'online'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <ShoppingBag className={`w-6 h-6 mx-auto mb-2 ${
                  searchType === 'online' ? 'text-indigo-600' : 'text-slate-400'
                }`} />
                <p className={`text-sm font-medium ${
                  searchType === 'online' ? 'text-indigo-900' : 'text-slate-600'
                }`}>
                  {t('tracking.onlineTab')}
                </p>
                <p className="text-xs text-slate-500 mt-1">{t('tracking.onlineTabDesc')}</p>
              </button>

              <button
                type="button"
                onClick={() => setSearchType('invoice')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  searchType === 'invoice'
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileText className={`w-6 h-6 mx-auto mb-2 ${
                  searchType === 'invoice' ? 'text-amber-600' : 'text-slate-400'
                }`} />
                <p className={`text-sm font-medium ${
                  searchType === 'invoice' ? 'text-amber-900' : 'text-slate-600'
                }`}>
                  {t('tracking.invoiceTab')}
                </p>
                <p className="text-xs text-slate-500 mt-1">{t('tracking.invoiceTabDesc')}</p>
              </button>
            </div>

            {/* Formulario */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('tracking.codeLabel')}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="DEV-0001 / GAR-0001 / DOA-0001 / TRA-0001"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                />
              </div>

              {searchType === 'online' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('tracking.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('tracking.nifLabel')}
                  </label>
                  <input
                    type="text"
                    value={nif}
                    onChange={(e) => setNif(e.target.value.toUpperCase())}
                    placeholder="12345678A / B12345678"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono"
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 ${
                  searchType === 'online'
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                } disabled:opacity-50`}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('common.searching')}
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    {t('tracking.searchButton')}
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Resultado del tracking */}
        {tracking && (
          <div className="space-y-6">
            {/* Status card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className={`${statusConfig?.bgColor || 'bg-slate-100'} p-6`}>
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full bg-white shadow-sm`}>
                    <StatusIcon className={`w-8 h-8 ${statusConfig?.color || 'text-slate-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm opacity-75">{t('tracking.currentStatus')}</p>
                    <h2 className={`text-2xl font-bold ${statusConfig?.color || 'text-slate-900'}`}>
                      {getStatusLabel(tracking.status, tracking.status_label)}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.returnCode')}</p>
                    <p className="text-lg font-mono font-bold text-slate-900">{tracking.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.originalOrder')}</p>
                    <p className="text-lg font-medium text-slate-900">#{tracking.order_reference}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.requestDate')}</p>
                    <p className="text-slate-900">{formatDate(tracking.created_at)}</p>
                  </div>
                  {tracking.case_type === 'return' ? (
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.refundAmount')}</p>
                    <p className="text-lg font-bold text-green-600">{formatMoney(tracking.refund_amount)}</p>
                  </div>
                  ) : tracking.case_type === 'warranty' ? (
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.warrantyResolution')}</p>
                    <p className="text-sm font-medium text-amber-600">{t('tracking.warrantyPending')}</p>
                  </div>
                  ) : (
                  <div>
                    <p className="text-sm text-slate-500">{t('tracking.incidentResolution')}</p>
                    <p className="text-sm font-medium text-blue-600">{t('tracking.incidentReplacement')}</p>
                  </div>
                  )}
                </div>

                {/* Desglose importes (solo devoluciones) */}
                {tracking.case_type === 'return' && tracking.shipping_cost > 0 && !tracking.refund_adjustment_type && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500">
                      {t('tracking.productAmount')} {formatMoney(tracking.product_amount)} - {t('tracking.shippingCost')} {formatMoney(tracking.shipping_cost)}
                    </p>
                  </div>
                )}
                {tracking.case_type === 'return' && tracking.refund_adjustment_type === 'waive_return_shipping' && tracking.shipping_cost > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-green-600 font-medium">
                      {t('tracking.shippingWaived')}
                    </p>
                  </div>
                )}
                {tracking.case_type === 'return' && tracking.refund_adjustment_type === 'waive_all_shipping' && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-green-600 font-medium">{t('tracking.shippingWaived')}</p>
                      {tracking.original_order_shipping != null && tracking.original_order_shipping > 0 && (
                        <p className="text-xs text-green-700 mt-1">
                          {t('tracking.originalShippingRefunded')} {formatMoney(tracking.original_order_shipping)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tracking envio */}
                {tracking.pickup_tracking && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{t('tracking.pickupTracking')}</p>
                        <p className="font-mono text-slate-900">{tracking.pickup_tracking}</p>
                      </div>
                      {getTrackingUrl() && (
                        <a
                          href={getTrackingUrl()!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm flex items-center gap-2"
                        >
                          <Truck className="w-4 h-4" />
                          {t('tracking.viewTracking')}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Rechazo */}
                {tracking.rejection_reason && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="bg-red-50 rounded-xl p-4">
                      <p className="font-medium text-red-800 mb-1">{t('tracking.rejectionReason')}</p>
                      <p className="text-sm text-red-700">{tracking.rejection_reason}</p>
                    </div>
                  </div>
                )}

                {/* Reembolsado */}
                {tracking.refunded_at && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="font-medium text-green-800 mb-1">{t('tracking.refundProcessed')}</p>
                      <p className="text-sm text-green-700">
                        {t('tracking.refundDesc', { amount: formatMoney(tracking.refund_amount), date: formatDate(tracking.refunded_at) })}
                        {tracking.refund_method === 'transfer' && t('tracking.refundByTransfer')}
                        {tracking.refund_method === 'store_credit' && t('tracking.refundByCredit')}
                        {tracking.refund_method === 'redsys' && t('tracking.refundByCard')}
                      </p>
                      {tracking.transfer_receipt && (
                        <a
                          href={`/api/returns/track/${tracking.code}/transfer-receipt?email=${encodeURIComponent(email)}&nif=${encodeURIComponent(nif)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 mt-2 text-sm text-green-700 hover:text-green-900 underline"
                        >
                          <FileText className="w-4 h-4" />
                          {t('tracking.viewReceipt')}
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Envío de sustitución (replacement) */}
                {tracking.status === 'replacement' && tracking.replacement_tracking && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="bg-orange-50 rounded-xl p-4">
                      <p className="font-medium text-orange-800 mb-1">{t('tracking.replacementTracking')}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div>
                          {tracking.replacement_carrier && (
                            <p className="text-xs text-orange-600 mb-1">{tracking.replacement_carrier.toUpperCase()}</p>
                          )}
                          <p className="font-mono text-orange-900">{tracking.replacement_tracking}</p>
                        </div>
                        {getReplacementTrackingUrl() && (
                          <a
                            href={getReplacementTrackingUrl()!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm flex items-center gap-2"
                          >
                            <Truck className="w-4 h-4" />
                            {t('tracking.viewTracking')}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mensaje contextual + Proximo paso */}
            {(() => {
              const ct = tracking.case_type || 'return'
              const prefix = ct === 'warranty' ? 'warranty' : ct === 'doa' ? 'incident' : ct === 'transport' ? 'incident' : ''
              const msgKey = prefix ? `${prefix}StatusMessages.${tracking.status}` : `statusMessages.${tracking.status}`
              const nextKey = prefix ? `${prefix}NextSteps.${tracking.status}` : `nextSteps.${tracking.status}`
              const hasNext = t(nextKey) !== nextKey && t(nextKey)
              if (!hasNext) return null
              return (
              <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
                    <ArrowRight className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-indigo-900 mb-1">{t('tracking.nextStep')}</p>
                    <p className="text-sm text-indigo-700">{t(msgKey) !== msgKey ? t(msgKey) : t(`statusMessages.${tracking.status}`)}</p>
                    <p className="text-sm text-indigo-600 mt-2 font-medium">{t(nextKey)}</p>
                  </div>
                </div>
              </div>
              )
            })()}

            {/* Timeline */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                {t('tracking.history')}
              </h3>
              <div className="space-y-0">
                {tracking.history.map((event, idx) => {
                  const eventConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.requested
                  const EventIcon = eventConfig.icon
                  const isLatest = idx === tracking.history.length - 1

                  return (
                    <div key={idx} className="relative pl-10 pb-6 last:pb-0">
                      {/* Linea */}
                      {idx < tracking.history.length - 1 && (
                        <div className="absolute left-[14px] top-9 bottom-0 w-[3px] bg-slate-200 rounded-full" />
                      )}

                      {/* Punto */}
                      <div className={`absolute left-0 top-1 w-[30px] h-[30px] rounded-full ${eventConfig.bgColor} flex items-center justify-center ${isLatest ? 'ring-4 ring-offset-1 ring-indigo-100' : ''}`}>
                        <EventIcon className={`w-4 h-4 ${eventConfig.color}`} />
                      </div>

                      {/* Contenido */}
                      <div>
                        <p className={`font-medium ${eventConfig.color}`}>{getStatusLabel(event.status, event.status_label)}</p>
                        <p className="text-sm text-slate-600 mt-0.5">{event.description}</p>
                        {isLatest && (() => {
                          const ct = tracking.case_type || 'return'
                          const prefix = ct === 'warranty' ? 'warranty' : (ct === 'doa' || ct === 'transport') ? 'incident' : ''
                          const mk = prefix ? `${prefix}StatusMessages.${event.status}` : `statusMessages.${event.status}`
                          const fallback = `statusMessages.${event.status}`
                          const msg = t(mk) !== mk ? t(mk) : (t(fallback) !== fallback ? t(fallback) : null)
                          return msg ? <p className="text-sm text-indigo-600 mt-1">{msg}</p> : null
                        })()}
                        <p className="text-xs text-slate-400 mt-1">{formatDate(event.created_at)}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Mensajes del equipo */}
            {tracking.comments && tracking.comments.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-indigo-500" />
                  {t('tracking.messages')}
                </h3>
                <div className="space-y-3">
                  {tracking.comments.map((comment: any, idx: number) => (
                    <div key={idx} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                      <p className="text-sm text-slate-700">{comment.comment}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                        <span>{comment.user_name}</span>
                        <span>·</span>
                        <span>{formatDate(comment.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buscar otra */}
            <div className="text-center">
              <button
                onClick={() => {
                  setIsAuthenticated(false)
                  setTracking(null)
                  setCode('')
                  setEmail('')
                  setNif('')
                  window.history.pushState({}, '', '/returns/track')
                }}
                className="text-indigo-600 hover:text-indigo-800 text-sm"
              >
                {t('tracking.checkAnother')}
              </button>
            </div>

            {/* Info adicional */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-slate-900 mb-4">{t('tracking.needHelp')}</h3>
              <p className="text-slate-600 text-sm mb-4">
                {t('tracking.helpDesc')}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <a href="mailto:info@jvs-informatica.com" className="text-indigo-600 hover:underline">
                  info@jvs-informatica.com
                </a>
                <span className="text-slate-400">|</span>
                <a href="tel:942719735" className="text-indigo-600 hover:underline">
                  942 71 97 35
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Empty state (solo si no hay tracking y no está buscando) */}
        {!tracking && !loading && isAuthenticated === false && !urlCode && (
          <div className="text-center py-8">
            <div className="inline-flex p-4 bg-slate-100 rounded-full mb-4">
              <Search className="w-10 h-10 text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">
              {t('tracking.emptyState')}
            </p>
          </div>
        )}

        {/* Link a solicitar */}
        <div className="text-center mt-8">
          <Link
            to="/returns"
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('tracking.requestReturn')}
          </Link>
        </div>
      </div>

      {/* WhatsApp flotante */}
      <a
        href="https://wa.me/34644464960?text=Hola,%20tengo%20una%20consulta%20sobre%20mi%20devolucion"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all z-50"
        title={t('common.whatsappTitle')}
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Footer */}
      <footer className="py-8 text-center text-sm text-slate-500">
        <p>{t('common.footer')}</p>
        <p className="mt-1">{t('common.footerContact')}</p>
      </footer>
    </div>
  )
}
